// ABOUTME: Selects WhatsApp templates capable of requesting a screening questionnaire.
// ABOUTME: Applies the job link settings that control the effective application flow.
const SCREENING_QUESTIONNAIRE_REQUEST_TEMPLATE_KEY =
    "candidate_screening_questionnaire_request";
const WHATSAPP_APPLICATION_LINK_SOURCE_KEY = "job.whatsappApplicationLink";
const FIXED_TEMPLATE_PARAMETER_COUNTS: Record<string, number> = {
    candidate_application_received: 2,
    candidate_screening_questionnaire_request: 3,
    candidate_process_update: 2,
    candidate_process_closed: 2,
    candidate_offer_next_steps: 2,
    candidate_new_opportunity: 3,
    candidate_interview_scheduled: 4,
    candidate_interview_reminder: 4,
    candidate_assessment_request: 4
};
const MESSAGE_TYPE_PARAMETER_COUNTS: Record<string, number> = {
    communicationApplicant: 4,
    communicationApplicantJob: 5,
    recruiting_process: 5,
    interview: 5,
    assessment: 5,
    new_opportunity: 3,
    custom: 0
};

const getTemplateName = (template: Compleo.IObject) =>
    String(template.templateName || template.name || "");

const getTemplateComponents = (template: Compleo.IObject) => {
    if (Array.isArray(template.components)) {
        return template.components;
    }
    if (Array.isArray(template.rawTemplate?.components)) {
        return template.rawTemplate.components;
    }

    return [
        template.headerText
            ? { type: "HEADER", text: template.headerText }
            : null,
        template.bodyText ? { type: "BODY", text: template.bodyText } : null
    ].filter(Boolean) as Compleo.IObject[];
};

const getTemplatePlaceholders = (
    template: Compleo.IObject
): Array<{ componentType: string; index: number }> =>
    getTemplateComponents(template).flatMap((component: Compleo.IObject) => {
        const componentType = String(component.type || "").toLowerCase();
        if (!["header", "body"].includes(componentType)) {
            return [];
        }

        const indexes = new Set<number>();
        for (const match of String(component.text || "").matchAll(
            /{{(\d+)}}/g
        )) {
            indexes.add(Number(match[1]));
        }

        return [...indexes].sort((left, right) => left - right).map((index) => ({
            componentType,
            index
        }));
    });

const hasMappedApplicationLinkParameter = (template: Compleo.IObject) => {
    const placeholders = getTemplatePlaceholders(template);
    const sourceGlobalTemplateKey = String(
        template.sourceGlobalTemplateKey || ""
    );
    const templateName = getTemplateName(template);
    const fixedIdentity = Object.prototype.hasOwnProperty.call(
        FIXED_TEMPLATE_PARAMETER_COUNTS,
        sourceGlobalTemplateKey
    )
        ? sourceGlobalTemplateKey
        : Object.prototype.hasOwnProperty.call(
              FIXED_TEMPLATE_PARAMETER_COUNTS,
              templateName
          )
        ? templateName
        : "";
    const canonicalParameterCount =
        FIXED_TEMPLATE_PARAMETER_COUNTS[fixedIdentity] ??
        MESSAGE_TYPE_PARAMETER_COUNTS[String(template.messageType || "")] ??
        0;

    if (canonicalParameterCount >= placeholders.length) {
        return (
            fixedIdentity === SCREENING_QUESTIONNAIRE_REQUEST_TEMPLATE_KEY &&
            placeholders.length === 3
        );
    }

    const definitions = template.parameterDefinitions || [];
    const resolvedDefinitions: Array<Compleo.IObject | undefined> =
        placeholders.map((placeholder) =>
            definitions.find(
                (definition: Compleo.IObject) =>
                    Number(definition.index) === placeholder.index &&
                    (!definition.componentType ||
                        definition.componentType === placeholder.componentType)
            )
        );
    if (
        resolvedDefinitions.some(
            (definition) =>
                !definition ||
                !definition.sourceKey ||
                definition.sourceType === "MANUAL"
        )
    ) {
        return false;
    }

    return resolvedDefinitions.some(
        (definition) =>
            definition?.sourceKey === WHATSAPP_APPLICATION_LINK_SOURCE_KEY
    );
};

export const canRequestScreeningQuestionnaireWithWhatsappTemplate = (
    template: Compleo.IObject
) => hasMappedApplicationLinkParameter(template);

export const getWhatsappScreeningQuestionnaireRequestPreviewTemplate = (
    template: Compleo.IObject | null,
    suggestedTemplateKey: string
) => ({
    templateName: template ? getTemplateName(template) : suggestedTemplateKey,
    languageCode: String(template?.language || "pt_BR")
});

export const getWhatsappScreeningQuestionnaireRequestTemplates = (
    templates: Compleo.IObject[]
) =>
    templates
        .filter(
            (template) =>
                template.status === "APPROVED" &&
                canRequestScreeningQuestionnaireWithWhatsappTemplate(template)
        )
        .sort((left, right) => {
            const leftIsGlobal =
                left.sourceGlobalTemplateKey ===
                SCREENING_QUESTIONNAIRE_REQUEST_TEMPLATE_KEY;
            const rightIsGlobal =
                right.sourceGlobalTemplateKey ===
                SCREENING_QUESTIONNAIRE_REQUEST_TEMPLATE_KEY;
            if (leftIsGlobal !== rightIsGlobal) {
                return leftIsGlobal ? -1 : 1;
            }

            return getTemplateName(left).localeCompare(getTemplateName(right));
        });

export type WhatsappScreeningQuestionnaireRequestTemplateSetupState =
    | "LOADING"
    | "ERROR"
    | "READY"
    | "PENDING"
    | "REJECTED"
    | "UNAVAILABLE"
    | "MISSING";

export const getWhatsappScreeningQuestionnaireRequestTemplateSetup = (
    templates: Compleo.IObject[],
    templatesStatus: "initial" | "fetching" | "success" | "error" = "success"
) => {
    if (templatesStatus === "error") {
        return {
            selectableTemplates: [],
            setupState: "ERROR" as const,
            setupTemplates: []
        };
    }

    if (templatesStatus !== "success") {
        return {
            selectableTemplates: [],
            setupState: "LOADING" as const,
            setupTemplates: []
        };
    }

    const compatibleTemplates = templates.filter(
        canRequestScreeningQuestionnaireWithWhatsappTemplate
    );
    const selectableTemplates =
        getWhatsappScreeningQuestionnaireRequestTemplates(templates);

    if (selectableTemplates.length > 0) {
        return {
            selectableTemplates,
            setupState: "READY" as const,
            setupTemplates: []
        };
    }

    const pendingTemplates = compatibleTemplates.filter(
        (template) => template.status === "PENDING"
    );
    if (pendingTemplates.length > 0) {
        return {
            selectableTemplates,
            setupState: "PENDING" as const,
            setupTemplates: pendingTemplates
        };
    }

    const rejectedTemplates = compatibleTemplates.filter(
        (template) => template.status === "REJECTED"
    );
    if (rejectedTemplates.length > 0) {
        return {
            selectableTemplates,
            setupState: "REJECTED" as const,
            setupTemplates: rejectedTemplates
        };
    }

    if (compatibleTemplates.length > 0) {
        return {
            selectableTemplates,
            setupState: "UNAVAILABLE" as const,
            setupTemplates: compatibleTemplates
        };
    }

    return {
        selectableTemplates,
        setupState: "MISSING" as const,
        setupTemplates: []
    };
};
